import { useCallback, useEffect, useRef, useState } from 'react';
import { get, streamUrl, DEMO } from './api';

export function useApi<T = any>(path: string | null, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(path));
  const seq = useRef(0);
  const load = useCallback(async (quiet = false) => {
    if (!path) return;
    const id = ++seq.current;
    if (!quiet) setLoading(true);
    try {
      const d = await get<T>(path);
      if (id === seq.current) { setData(d); setError(null); }
    } catch (e: any) {
      if (id === seq.current) setError(e.message ?? 'Something went wrong.');
    } finally {
      if (id === seq.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { void load(); }, [load]);
  return { data, error, loading, reload: () => load(true), setData };
}

export type LiveEvent = { type: string; conversationId?: string; approvalId?: string; commentId?: string; level?: string; title?: string; body?: string };
type Handler = (e: LiveEvent) => void;
const handlers = new Set<Handler>();
let source: EventSource | null = null;
let statusListeners = new Set<(s: boolean) => void>();
let connected = false;

function setConnected(v: boolean) { connected = v; statusListeners.forEach((l) => l(v)); }

export function startLive() {
  if (DEMO) { setConnected(true); return; }
  if (source) return;
  const open = () => {
    source = new EventSource(streamUrl());
    source.addEventListener('ready', () => setConnected(true));
    source.onmessage = (m) => { try { const e = JSON.parse(m.data); handlers.forEach((h) => h(e)); } catch { /* ignore */ } };
    source.onerror = () => { setConnected(false); source?.close(); source = null; setTimeout(open, 4000); };
  };
  open();
}
export function stopLive() { source?.close(); source = null; setConnected(false); }
export function emitDemoEvent(e: LiveEvent) { handlers.forEach((h) => h(e)); }

export function useLive(handler: Handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => { const h: Handler = (e) => ref.current(e); handlers.add(h); return () => { handlers.delete(h); }; }, []);
}
export function useLiveStatus() {
  const [s, set] = useState(connected);
  useEffect(() => { statusListeners.add(set); return () => { statusListeners.delete(set); }; }, []);
  return s;
}

export function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}
